import React, { ReactNode, useState } from 'react';
import { Button, Modal, ModalProps } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';

import { AccessControlWrapper } from '@/common/components';
import RejectPrompt from '@/common/RejectPrompt/RejectPrompt';
import { scribeInstrumentationEvents } from '@/common/Scribe/EventsList';
import { REQUESTED } from '@/constants';
import { AdaptiveExperiment, Experiment } from '@/lib/PlannerDTOs';
import { AccessTargetInput, UserAction } from '@/utils/accessControl';
import { isWhitelistedApprover } from '@/utils/experiment';
import { useAppNavigate } from '@/utils/navigation';

const RejectModal = ({
  open,
  onCancel,
  onOk,
  experiment,
}: ModalProps & { experiment: Experiment }) => {
  const { navigateTo } = useAppNavigate();

  if (!open) return null;

  return (
    <Modal
      title="Do you want to reject this experiment?" // only for REQUESTED
      open
      onCancel={onCancel}
      footer={null}
    >
      <RejectPrompt
        experiment={experiment?.name}
        onSuccess={() => {
          scribe.sendEvent(
            scribeInstrumentationEvents.clickRejectExperiment.name,
            {
              experiment: experiment?.name,
              status: experiment?.state,
              STATE_NAME:
                scribeInstrumentationEvents.clickRejectExperiment.state,
            },
          );

          onOk(null);

          navigateTo('/t/experiments');
        }}
        layout="vertical"
      />
    </Modal>
  );
};

const RejectWrapper = ({
  disabled = false,
  experiment,
  ghost,
  icon,
}: {
  disabled?: boolean;
  experiment: Experiment;
  ghost?: boolean;
  icon?: ReactNode;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <Button
        size="small"
        shape="round"
        onClick={() => {
          setOpen(true);
        }}
        disabled={disabled}
        ghost={ghost}
        icon={icon}
      >
        Reject
      </Button>
      <RejectModal
        experiment={experiment}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          setOpen(false);
        }}
      />
    </>
  );
};

const Reject = ({
  variableTechFamilies,
  experiment,
  adaptiveExperiment,
  ghost,
  icon,
  accessTargets,
}: {
  variableTechFamilies: string[];
  experiment: Experiment;
  adaptiveExperiment: AdaptiveExperiment;
  ghost?: boolean;
  icon?: ReactNode;
  accessTargets?: AccessTargetInput[];
}) => {
  const mainExperiment = experiment || adaptiveExperiment;

  if (mainExperiment?.state !== REQUESTED) return null;

  if (isWhitelistedApprover(experiment?.approvalFlow?.data || [])) {
    return <RejectWrapper experiment={mainExperiment} />;
  }

  return (
    <AccessControlWrapper
      action={UserAction.REJECT_CURRENT_EXPERIMENT}
      techFamilies={variableTechFamilies}
      accessTargets={accessTargets}
    >
      {(hasAccess: boolean) => (
        <RejectWrapper
          disabled={!hasAccess}
          ghost={ghost}
          icon={icon}
          experiment={mainExperiment}
        />
      )}
    </AccessControlWrapper>
  );
};

export default Reject;
